import * as OperatorLoginsState from '../state/OperatorLoginsState.ts';
import * as CurrentOperatorLoginState from '../../common/state/CurrentOperatorLoginState.ts';
import * as CommonOperatorLoginsState from '../../common/state/OperatorLoginsState.ts';
import { Permissions } from '../../common/state/OperatorLoginsState.ts';
import { ApiError } from 'dg-web-shared/dto/ApiError.ts';
import { UseFormReturn, useWatch } from 'react-hook-form';
import {
    Localized,
    useLanguage,
    useTranslation,
} from 'dg-web-shared/lib/Localization.tsx';
import {
    maxFieldErrorMessage,
    minFieldErrorMessage,
    tooManyCharsMessage,
} from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { Box, FormControlLabel, Stack, Switch } from '@mui/material';
import * as EmailValidator from 'email-validator';
import React, { type JSX, useState } from 'react';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { useStore, useUpdate } from 'dg-web-shared/lib/Flux.tsx';
import {
    useServerFetch,
    useServerSuccessEffect,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { ReactHookFormSwitch } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormSwitch.tsx';
import { OtpActivationNotPossibleWarning } from './LoginMeta.tsx';
import { OperatorParking } from '../../common/state/useParkingsFetch.ts';
import * as MasterDataZonesState from '../../common/state/MasterDataZonesState.ts';
import {
    gatherZoneGroups,
    selectableOffstreetZone,
    selectableOnstreetZone,
    SelectableZone,
} from '../../ui/slidein/ZonesSelectionSlideIn.tsx';
import { OperatorFormSelectMultiple } from '../../ui/form/OperatorFormSelect.tsx';
import { MultiSelectEntryType } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormSelect.tsx';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import { ProductType } from './OperatorLoginsList.tsx';
import * as PermitTypeState from '../../common/state/PermitTypeState.ts';
import { PermitType } from '../../common/state/PermitTypeState.ts';
import { RecodeCondition } from '../../cloud-connector/CloudConnectorRecodeQrCodesAdd.tsx';
import { OperatorTypo } from '../../ui/OperatorTypo.ts';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import { ParkingaboRole } from '../../parkingabo-role/OperatorParkingaboRole.ts';
import { ReadOnlyTextField } from '../../ui/material-ui/ReadOnlyTextField.tsx';
import {
    OtpDisableWaringModal,
    useUpdateOtpStatusServerWrite,
} from './OperatorLoginProfilePage.tsx';
import { EmailSuppressionTag } from '../../shared-mui-components/EmailSuppressionTag.tsx';
import { EmailSuppression } from '../../common/models/EmailSuppression.ts';

const MAX_LENGTH_SHORT = 25;
const MAX_LENGTH_LONG = 50;

export enum OperatorLoginDetailFormFields {
    username = 'username',
    firstname = 'firstname',
    lastname = 'lastname',
    description = 'description',
    email = 'email',
    telnr = 'telnr',
    sendPasswordLink = 'sendPasswordLink',
    allowedPermitTypes = 'allowedPermitTypes',
    allowedProductTypes = 'allowedProductTypes',
    allowedZones = 'allowedZones',
    allowedRecodeConditions = 'allowedRecodeConditions',
    allowedParkingaboRoles = 'allowedParkingaboRoles',
    permissions = 'permissions',
    softAnonymizeCheckinPublicPermitAfterDays = 'softAnonymizeCheckinPublicPermitAfterDays',
}

export interface OperatorLoginDetailFormValues {
    [OperatorLoginDetailFormFields.username]: string;
    [OperatorLoginDetailFormFields.firstname]: string;
    [OperatorLoginDetailFormFields.lastname]: string;
    [OperatorLoginDetailFormFields.description]: string | null;
    [OperatorLoginDetailFormFields.email]: string;
    [OperatorLoginDetailFormFields.telnr]: string | null;
    [OperatorLoginDetailFormFields.sendPasswordLink]: boolean;
    [OperatorLoginDetailFormFields.allowedPermitTypes]: number[];
    [OperatorLoginDetailFormFields.allowedProductTypes]: number[];
    [OperatorLoginDetailFormFields.allowedZones]: number[];
    [OperatorLoginDetailFormFields.allowedRecodeConditions]: string[];
    [OperatorLoginDetailFormFields.allowedParkingaboRoles]: string[];
    [OperatorLoginDetailFormFields.permissions]: Permissions;
    [OperatorLoginDetailFormFields.softAnonymizeCheckinPublicPermitAfterDays]:
        | number
        | null;
}

export interface ApiErrorInterface {
    message: ApiError;
    code: number;
}

export function operatorLoginPermissionFieldName(
    permission: keyof Permissions,
) {
    return `${OperatorLoginDetailFormFields.permissions}.${permission}` as const;
}

export function OperatorLoginDetailUserContent({
    form,
    readonly,
    mandatoryFieldMessage,
    isAdmin,
    maxSoftAnonymizeCheckinPublicPermitAfterDays,
    isCreate,
    emailSuppression,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
    mandatoryFieldMessage: string;
    isAdmin: boolean;
    maxSoftAnonymizeCheckinPublicPermitAfterDays: number;
    isCreate: boolean;
    emailSuppression: EmailSuppression | null;
}) {
    const {
        operatorData: { mgmtOrgAddress },
    } = useOperatorContext();
    const longTooManyCharsMessage = useTranslation(
        tooManyCharsMessage(MAX_LENGTH_LONG),
    );
    const shortTooManyCharsMessage = useTranslation(
        tooManyCharsMessage(MAX_LENGTH_SHORT),
    );
    const emailInvalidMessage = useTranslation({
        de: 'E-Mail nicht gültig.',
        fr: 'E-mail non valide.',
        it: 'E-mail non valida.',
        en: 'Email not valid.',
    });
    const phoneInvalidCharsMessage = useTranslation({
        de: 'Es sind nur Zahlen, + und Leerzeichen zulässig.',
        fr: 'Seuls les chiffres, + et les espaces sont autorisés.',
        it: 'Sono consentiti solo numeri, + e spazi.',
        en: 'Only numbers, +, and spaces are allowed.',
    });
    return (
        <>
            <ParkingaboLoginDivideBar
                title={
                    <Localized
                        de="Benutzer"
                        fr="Utilisateur"
                        it="Utente"
                        en="User"
                    />
                }
            />
            <ReactHookFormTextField
                readonly={readonly}
                label={
                    <Localized
                        de="Benutzername"
                        fr="Nom utilisateur"
                        it="Nome utente"
                        en="Username"
                    />
                }
                name={OperatorLoginDetailFormFields.username}
                control={form.control}
                rules={{
                    required: {
                        value: true,
                        message: mandatoryFieldMessage,
                    },
                }}
                onChange={data => {
                    form.setValue(
                        OperatorLoginDetailFormFields.username,
                        data.target.value.replace(/\s/g, ''),
                    );
                }}
                trimOnBlur={true}
            />
            <Stack direction={'row'} spacing={2}>
                <ReactHookFormTextField
                    readonly={readonly}
                    label={
                        <Localized
                            de="Nachname"
                            fr="Nom"
                            it="Cognome"
                            en="Lastname"
                        />
                    }
                    name={OperatorLoginDetailFormFields.lastname}
                    control={form.control}
                    rules={{
                        required: {
                            value: true,
                            message: mandatoryFieldMessage,
                        },
                    }}
                    trimOnBlur={true}
                    style={{
                        flexGrow: 1,
                    }}
                />
                <ReactHookFormTextField
                    readonly={readonly}
                    label={
                        <Localized
                            de="Vorname"
                            fr="Prénom"
                            it="Nome"
                            en="Firstname"
                        />
                    }
                    name={OperatorLoginDetailFormFields.firstname}
                    control={form.control}
                    rules={{
                        required: {
                            value: true,
                            message: mandatoryFieldMessage,
                        },
                    }}
                    trimOnBlur={true}
                    style={{
                        flexGrow: 1,
                    }}
                />
            </Stack>
            {isCreate && (
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Organisation"
                            fr="Organisation"
                            it="Organizzazione"
                            en="Organization"
                        />
                    }
                    value={
                        <>
                            {mgmtOrgAddress.name1}
                            {mgmtOrgAddress.name2 && <br />}
                            {mgmtOrgAddress.name2}
                        </>
                    }
                />
            )}
            <ReactHookFormTextField
                readonly={readonly}
                label={
                    <Localized
                        de="Beschreibung"
                        fr="Description"
                        it="Descrizione"
                        en="Description"
                    />
                }
                name={OperatorLoginDetailFormFields.description}
                control={form.control}
                trimOnBlur={true}
            />
            <ReactHookFormTextField
                readonly={readonly}
                label={
                    <Localized de="E-Mail" fr="E-mail" it="E-mail" en="Email" />
                }
                name={OperatorLoginDetailFormFields.email}
                control={form.control}
                rules={{
                    validate: {
                        isEmailValid: (value: string | null) =>
                            value !== '' && value !== null
                                ? EmailValidator.validate(value) ||
                                  emailInvalidMessage
                                : true,
                    },
                    maxLength: {
                        value: MAX_LENGTH_LONG,
                        message: longTooManyCharsMessage,
                    },
                    required: {
                        value: true,
                        message: mandatoryFieldMessage,
                    },
                }}
                trimOnBlur={true}
                onChange={() =>
                    form.setValue(
                        OperatorLoginDetailFormFields.sendPasswordLink,
                        true,
                    )
                }
            />
            <EmailSuppressionTag
                suppression={emailSuppression}
                tooltipPlacement={'right'}
            />
            <ReactHookFormTextField
                readonly={readonly}
                label={
                    <Localized
                        de="Telefon"
                        fr="Téléphone"
                        it="Telefono"
                        en="Phone"
                    />
                }
                name={OperatorLoginDetailFormFields.telnr}
                control={form.control}
                rules={{
                    maxLength: {
                        value: MAX_LENGTH_SHORT,
                        message: shortTooManyCharsMessage,
                    },
                    validate: {
                        isPhoneValid: (value: string) => {
                            if (value === '') {
                                return true;
                            }

                            if (!/^[+0-9][0-9 ]*$/.test(value)) {
                                return phoneInvalidCharsMessage;
                            }

                            return true;
                        },
                    },
                }}
                trimOnBlur={true}
            />
            {isAdmin && !isCreate && (
                <ReactHookFormSwitch
                    label={
                        <Localized
                            de="Superuser (Benutzer verwalten)"
                            fr="Superutilisateur (gérer les utilisateurs)"
                            it="Superuser (gestire gli utenti)"
                            en="Superuser (manage users)"
                        />
                    }
                    name={operatorLoginPermissionFieldName(
                        'userPermissionEdit',
                    )}
                    control={form.control}
                    readonly={readonly}
                    onChange={(_e, checked) => {
                        if (checked) {
                            form.setValue(
                                OperatorLoginDetailFormFields.softAnonymizeCheckinPublicPermitAfterDays,
                                maxSoftAnonymizeCheckinPublicPermitAfterDays,
                            );
                            form.setValue(
                                operatorLoginPermissionFieldName(
                                    'operatorAppActive',
                                ),
                                true as never,
                            );
                        }
                    }}
                />
            )}
        </>
    );
}

export function OperatorLoginDetailSecurityContent({
    form,
    readonly,
    isDirty,
    isCreate,
    otpEnabled,
    operatorLoginId,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
    isDirty: boolean;
    isCreate: boolean;
    otpEnabled: boolean;
    operatorLoginId: number | null;
}) {
    const [
        otpActivationNotPossibleWarningOpen,
        setOtpActivationNotPossibleWarningOpen,
    ] = useState(false);
    const [disableOtpWarningOpen, setDisableOtpWarningOpen] = useState(false);
    const [updateOtpEnabledState, updateOtpEnabled] =
        useUpdateOtpStatusServerWrite(operatorLoginId);
    const update = useUpdate();
    useServerSuccessEffect(updateOtpEnabledState, data => {
        if (data == null) {
            update(store => {
                OperatorLoginsState.Edit.reset(store);
                CommonOperatorLoginsState.List.reset(store);
                CurrentOperatorLoginState.reset(store);
                return 'refetch-logins-after-otp-disable';
            });
        }
    });
    return (
        <Stack spacing={1}>
            <ParkingaboLoginDivideBar
                title={
                    <Localized
                        de="Sicherheit"
                        fr="Sécurité"
                        it="Sicurezza"
                        en="Security"
                    />
                }
            />
            <ReactHookFormSwitch
                label={
                    isCreate ? (
                        <Localized
                            de="Bestätigung (inkl. Passwort-Link) versenden"
                            fr="Envoyer la confirmation (y compris le lien mot de passe)"
                            it="Inviare conferma (incl. link password)"
                            en="Send confirmation (incl. password link)"
                        />
                    ) : (
                        <Localized
                            de="Passwort-Link versenden"
                            fr="Envoyer le lien de mot de passe"
                            it="Inviare il link per la password"
                            en="Send password link"
                        />
                    )
                }
                name={OperatorLoginDetailFormFields.sendPasswordLink}
                control={form.control}
                readonly={readonly}
            />
            {!isCreate && (
                <>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={otpEnabled}
                                onChange={() =>
                                    otpEnabled
                                        ? setDisableOtpWarningOpen(true)
                                        : setOtpActivationNotPossibleWarningOpen(
                                              true,
                                          )
                                }
                                disabled={isDirty || !operatorLoginId}
                            />
                        }
                        label={
                            <Localized
                                de="Zwei-Faktor-Authentifizierung"
                                fr="Authentification à deux facteurs"
                                it="Autenticazione a due fattori"
                                en="Two-Factor Authentication"
                            />
                        }
                    />
                    <OtpActivationNotPossibleWarning
                        open={otpActivationNotPossibleWarningOpen}
                        onConfirm={() =>
                            setOtpActivationNotPossibleWarningOpen(false)
                        }
                    />
                    <OtpDisableWaringModal
                        open={disableOtpWarningOpen}
                        onCancel={() => setDisableOtpWarningOpen(false)}
                        onConfirm={() =>
                            updateOtpEnabled({
                                enable: false,
                            })
                        }
                    />
                </>
            )}
        </Stack>
    );
}

export function OperatorLoginSuperUserAccessRights({
    form,
    readonly,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
}) {
    const { operatorData } = useOperatorContext();
    return (
        <Stack spacing={1}>
            <ParkingaboLoginDivideBar
                title={
                    <Localized
                        de="E-Mail-Benachrichtigungen"
                        fr="Notifications e-mail"
                        it="Notifiche e-mail"
                        en="E-mail alerts"
                    />
                }
            />

            {operatorData.hasTaxomexModule && (
                <ReactHookFormSwitch
                    label={
                        <Localized
                            de="TOMeco"
                            fr="TOMeco"
                            it="TOMeco"
                            en="TOMeco"
                        />
                    }
                    name={operatorLoginPermissionFieldName(
                        'parkingMeterAlerts',
                    )}
                    control={form.control}
                    readonly={readonly}
                />
            )}
            {operatorData.hasCloudConnector && (
                <ReactHookFormSwitch
                    label={
                        <Localized
                            de="Cloud Connector"
                            fr="Cloud Connector"
                            it="Cloud Connector"
                            en="Cloud Connector"
                        />
                    }
                    name={operatorLoginPermissionFieldName(
                        'cloudConnectorAlerts',
                    )}
                    control={form.control}
                    readonly={readonly}
                />
            )}
            <ParkingaboLoginDivideBar
                title={
                    <Localized
                        de="Zugriffsrechte"
                        fr="Droits d'accès"
                        it="Diritti d'accesso"
                        en="Access Rights"
                    />
                }
            />
            <ReactHookFormTextField
                readonly
                label={
                    <Localized
                        de="Anonymisierung der Parkvorgänge nach (Tagen)"
                        fr="Anonymiser les procédures plus anciennes que (jours)"
                        it="Rendere anonime le procedure più vecchie di (giorni)"
                        en="Anonymise transactions after (days)"
                    />
                }
                type="number"
                name={
                    OperatorLoginDetailFormFields.softAnonymizeCheckinPublicPermitAfterDays
                }
                control={form.control}
            />
        </Stack>
    );
}

function zones(
    zones: MasterDataZonesState.State,
    parkings: OperatorParking[],
    filterZonesWithoutRelevanceTag: boolean,
): SelectableZone[] {
    return zones.data
        .filter(z =>
            filterZonesWithoutRelevanceTag
                ? !z.isFromForeignMandant
                : !z.isFromForeignMandant &&
                  (z.relevance.tag ===
                      MasterDataZonesState.MasterDataZoneRelevanceTag.ACTIVE ||
                      (z.relevance.tag ===
                          MasterDataZonesState.MasterDataZoneRelevanceTag
                              .HAS_NON_ACTIVE_USAGES &&
                          z.relevance.hasCheckIns)) &&
                  z.transactionType !== null,
        )
        .map(selectableOnstreetZone)
        .concat(parkings.map(selectableOffstreetZone));
}

export function OperatorLoginZoneSelect({
    form,
    readonly,
    parkings,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
    parkings: OperatorParking[];
}) {
    const { storeState } = useStore(s => ({
        zones: MasterDataZonesState.get(s),
    }));
    const selectableZones = zones(storeState.zones, parkings, true);
    const groups = gatherZoneGroups(selectableZones);
    const allowedZones = useWatch({
        control: form.control,
        name: OperatorLoginDetailFormFields.allowedZones,
    });
    const hasMissingZonePermission = selectableZones.some(
        zone => !allowedZones.includes(zone.id),
    );

    return (
        <Box sx={{ paddingBottom: theme => theme.spacing(1) }}>
            <OperatorFormSelectMultiple
                readonly={readonly}
                label={
                    <Localized
                        de="Zonen / Parkings"
                        fr="Zones / Parkings"
                        it="Zone / Parcheggi"
                        en="Zones / Parkings"
                    />
                }
                name={OperatorLoginDetailFormFields.allowedZones}
                control={form.control}
                options={groups.map(group => ({
                    type: MultiSelectEntryType.GROUP,
                    label: `${group[0].postalCode} ${group[0].place}`,
                    options: group.map(zone => ({
                        type: MultiSelectEntryType.ITEM,
                        display: <ZoneSelectDisplay zone={zone} />,
                        value: zone.id,
                    })),
                }))}
                infoBox={
                    hasMissingZonePermission ? (
                        <MultiSelectionInfoBox>
                            <Localized
                                de="Neue Zonen/Parkings werden nicht automatisch zugelassen."
                                fr="Les nouveaux zones/parkings ne sont pas automatiquement autorisés."
                                it="I nuovi zone/parcheggi non vengono autorizzati automaticamente."
                                en="New zones/parkings are not automatically allowed."
                            />
                        </MultiSelectionInfoBox>
                    ) : undefined
                }
            />
        </Box>
    );
}

function ZoneSelectDisplay({ zone }: { zone: SelectableZone }) {
    return (
        <Stack direction={'row'}>
            <Box sx={{ minWidth: '32px' }}>
                {zone.code == Number.MAX_SAFE_INTEGER ? ' ' : zone.code}
            </Box>
            {zone.name}
            {!zone.isActive && (
                <Localized
                    de=" — INAKTIV"
                    fr=" — INACTIVE"
                    it=" — INATTIVA"
                    en=" — INACTIVE"
                />
            )}
        </Stack>
    );
}

function MultiSelectionInfoBox({ children }: { children: React.ReactNode }) {
    return (
        <Box
            sx={theme => ({
                color: theme.palette.white.main,
                backgroundColor: 'orange',
                padding: theme.spacing(1),
                fontWeight: 500,
            })}
        >
            {children}
        </Box>
    );
}

export function OperatorLoginDetailParkingportal({
    form,
    readonly,
    operatorData,
    maxSoftAnonymizeCheckinPublicPermitAfterDays,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
    operatorData: OperatorDataState.OperatorData;
    maxSoftAnonymizeCheckinPublicPermitAfterDays: number;
}) {
    const operatorAppActiveFieldName =
        operatorLoginPermissionFieldName('operatorAppActive');
    const operatorAppActive = useWatch({
        control: form.control,
        name: operatorAppActiveFieldName,
    });
    const masterDataAndStatisticsFieldName = operatorLoginPermissionFieldName(
        'masterDataAndStatistics',
    );
    const masterDataAndStatistics = useWatch({
        control: form.control,
        name: masterDataAndStatisticsFieldName,
    });
    const activityStatementFieldName =
        operatorLoginPermissionFieldName('activityStatement');

    const minAnonymizeDays = 0;
    const minAnonymizeDaysMessage = useTranslation(
        minFieldErrorMessage(minAnonymizeDays),
    );
    const maxAnonymizeDaysMessage = useTranslation(
        maxFieldErrorMessage(maxSoftAnonymizeCheckinPublicPermitAfterDays),
    );
    const transactions = useWatch({
        control: form.control,
        name: operatorLoginPermissionFieldName('transactions'),
    });
    return (
        <Stack spacing={1}>
            <ParkingaboLoginDivideBar
                title={
                    <Localized
                        de="Parkingportal"
                        fr="Parkingportal"
                        it="Parkingportal"
                        en="Parkingportal"
                    />
                }
            />
            <ReactHookFormSwitch
                label={
                    <Localized de="Aktiv" fr="Actif" it="Attivo" en="Active" />
                }
                name={operatorAppActiveFieldName}
                control={form.control}
                readonly={readonly}
            />
            {operatorAppActive && (
                <>
                    <ReactHookFormSwitch
                        label={
                            <Localized
                                de="Stammdaten / Statistiken"
                                fr="Données de base / statistiques"
                                it="Dati di base / statistiche"
                                en="Master data / statistics"
                            />
                        }
                        name={masterDataAndStatisticsFieldName}
                        control={form.control}
                        readonly={readonly}
                        onChange={() => {
                            form.setValue(
                                activityStatementFieldName,
                                false as never,
                            );
                        }}
                    />
                    {masterDataAndStatistics && (
                        <ReactHookFormSwitch
                            label={
                                <Localized
                                    de="Kontoauszug"
                                    fr="Relevés de compte"
                                    it="Estratto conto"
                                    en="Account statement"
                                />
                            }
                            name={activityStatementFieldName}
                            control={form.control}
                            readonly={readonly}
                        />
                    )}
                    {(((operatorData.hasOnstreetTransactions ||
                        operatorData.existingOffstreetTransactionsType !=
                            null) &&
                        operatorData.hasParkingpayModule) ||
                        operatorData.hasTaxomexModule) && (
                        <>
                            <ReactHookFormSwitch
                                label={
                                    <Localized
                                        de={`Parkvorgänge${
                                            operatorData.hasTicketFunctionality
                                                ? ' / Ticket'
                                                : ''
                                        }`}
                                        fr={`Procédures de stationnement${
                                            operatorData.hasTicketFunctionality
                                                ? ' / Ticket'
                                                : ''
                                        }`}
                                        it={`Procedure di parcheggio${
                                            operatorData.hasTicketFunctionality
                                                ? ' / Ticket'
                                                : ''
                                        }`}
                                        en={`Parkings${
                                            operatorData.hasTicketFunctionality
                                                ? ' / Ticket'
                                                : ''
                                        }`}
                                    />
                                }
                                name={operatorLoginPermissionFieldName(
                                    'transactions',
                                )}
                                control={form.control}
                                readonly={readonly}
                                onChange={() => {
                                    form.setValue(
                                        operatorLoginPermissionFieldName(
                                            'refundParkTransactions',
                                        ),
                                        false as never,
                                    );
                                }}
                            />
                            {transactions && (
                                <ReactHookFormSwitch
                                    label={
                                        <Localized
                                            de={`Parkvorgänge${
                                                operatorData.hasTicketFunctionality
                                                    ? ' / Ticket'
                                                    : ''
                                            } - Zurückzahlen`}
                                            fr={`Procédures de stationnement${
                                                operatorData.hasTicketFunctionality
                                                    ? ' / Ticket'
                                                    : ''
                                            } - Rembourser`}
                                            it={`Rimborsare procedure${
                                                operatorData.hasTicketFunctionality
                                                    ? ' / Ticket'
                                                    : ''
                                            }`}
                                            en={`Parkings${
                                                operatorData.hasTicketFunctionality
                                                    ? ' / Ticket'
                                                    : ''
                                            } - Refund`}
                                        />
                                    }
                                    name={operatorLoginPermissionFieldName(
                                        'refundParkTransactions',
                                    )}
                                    control={form.control}
                                    readonly={readonly}
                                />
                            )}
                        </>
                    )}
                    <ReactHookFormTextField
                        label={
                            <Localized
                                de="Anonymisierung der Parkvorgänge nach (Tagen)"
                                fr="Anonymiser les procédures plus anciennes que (jours)"
                                it="Rendere anonime le procedure più vecchie di (giorni)"
                                en="Anonymise transactions after (days)"
                            />
                        }
                        type="number"
                        name={
                            OperatorLoginDetailFormFields.softAnonymizeCheckinPublicPermitAfterDays
                        }
                        control={form.control}
                        slotProps={{
                            htmlInput: {
                                step: 1,
                                min: minAnonymizeDays,
                            },
                        }}
                        rules={{
                            min: {
                                value: minAnonymizeDays,
                                message: minAnonymizeDaysMessage,
                            },
                            max: {
                                value: maxSoftAnonymizeCheckinPublicPermitAfterDays,
                                message: maxAnonymizeDaysMessage,
                            },
                            required: true,
                        }}
                    />
                    <ReactHookFormSwitch
                        label={
                            <Localized
                                de="Kontroll-History"
                                fr="Historique des contrôles"
                                it="Storico controlli"
                                en="Controll history"
                            />
                        }
                        name={operatorLoginPermissionFieldName(
                            'enforcementLog',
                        )}
                        control={form.control}
                        readonly={readonly}
                    />
                </>
            )}
        </Stack>
    );
}

export function OperatorLoginParkingCheckModule({
    form,
    readonly,
    operatorData,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
    operatorData: OperatorDataState.OperatorData;
}) {
    const enforcementAppActive = useWatch({
        control: form.control,
        name: operatorLoginPermissionFieldName('enforcementAppActive'),
    });
    return (
        <Stack spacing={1}>
            <ParkingaboLoginDivideBar title={<>Parkingcheck</>} />
            <ReactHookFormSwitch
                label={
                    <Localized de="Aktiv" fr="Actif" it="Attivo" en="Active" />
                }
                name={operatorLoginPermissionFieldName('enforcementAppActive')}
                control={form.control}
                readonly={readonly}
                onChange={() => {
                    form.setValue(
                        operatorLoginPermissionFieldName('twintQrCodePairing'),
                        false as never,
                    );
                }}
            />
            {enforcementAppActive && operatorData.hasParkingpayModule && (
                <ReactHookFormSwitch
                    label={
                        <Localized
                            de="Zuweisung TWINT QR-Code"
                            fr="Attribution code QR TWINT"
                            it="Assegnare codice QR TWINT"
                            en="TWINT qr code pairing"
                        />
                    }
                    name={operatorLoginPermissionFieldName(
                        'twintQrCodePairing',
                    )}
                    control={form.control}
                    readonly={readonly}
                />
            )}
        </Stack>
    );
}

export function OperatorLoginParkingpayModule({
    form,
    readonly,
    operatorData,
    productTypes,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
    operatorData: OperatorDataState.OperatorData;
    productTypes: ProductType[];
}) {
    const hasMoreThanOneModule =
        operatorData.hasParkingpayModule && operatorData.hasTaxomexModule;
    const parkingpayModule = useWatch({
        control: form.control,
        name: operatorLoginPermissionFieldName('parkingpayModule'),
    });
    const permitsRead = useWatch({
        control: form.control,
        name: operatorLoginPermissionFieldName('permitsRead'),
    });
    return (
        <Stack spacing={1}>
            <ParkingaboLoginDivideBar
                title={
                    <Localized
                        de="Digitale Kanäle"
                        fr="Canaux numériques"
                        it="Canali digitali"
                        en="Digital channels"
                    />
                }
            />
            {hasMoreThanOneModule && (
                <ReactHookFormSwitch
                    label={
                        <Localized
                            de="Modul digitale Kanäle"
                            fr="Module canaux numériques"
                            it="Modulo canali digitali"
                            en="Module digital channel"
                        />
                    }
                    name={operatorLoginPermissionFieldName('parkingpayModule')}
                    control={form.control}
                    readonly={readonly}
                    onChange={() => {
                        form.setValue(
                            operatorLoginPermissionFieldName(
                                'refundParkTransactions',
                            ),
                            false as never,
                        );
                        form.setValue(
                            operatorLoginPermissionFieldName('permitsRead'),
                            false as never,
                        );
                        form.setValue(
                            operatorLoginPermissionFieldName('permitsWrite'),
                            false as never,
                        );
                        form.setValue(
                            operatorLoginPermissionFieldName('refundPermits'),
                            false as never,
                        );
                        form.setValue(
                            operatorLoginPermissionFieldName('whitelistCreate'),
                            false as never,
                        );
                        form.setValue(
                            operatorLoginPermissionFieldName('whitelistDelete'),
                            false as never,
                        );
                        form.setValue(
                            operatorLoginPermissionFieldName(
                                'twintQrCodePairing',
                            ),
                            false as never,
                        );
                        form.setValue(
                            operatorLoginPermissionFieldName('enforcementLog'),
                            false as never,
                        );
                        form.setValue(
                            operatorLoginPermissionFieldName('quotasWrite'),
                            false as never,
                        );
                    }}
                />
            )}
            {parkingpayModule && (
                <>
                    {operatorData.licensePlatePermitSettings.active &&
                        operatorData.licensePlatePermitSettings
                            .whitelistActive && (
                            <>
                                <ReactHookFormSwitch
                                    label={
                                        <Localized
                                            de="Freigaben - Erfassen / Ändern"
                                            fr="Approbations - Saisir / modifier"
                                            it="Approvazioni - prodotti – Registrare / modificare"
                                            en="Whitelist - Create / Edit"
                                        />
                                    }
                                    name={operatorLoginPermissionFieldName(
                                        'whitelistCreate',
                                    )}
                                    control={form.control}
                                    readonly={readonly}
                                />
                                <ReactHookFormSwitch
                                    label={
                                        <Localized
                                            de="Freigaben - Löschen"
                                            fr="Approbations - Effacer"
                                            it="Approvazioni - Cancellare"
                                            en="Whitelist - Delete"
                                        />
                                    }
                                    name={operatorLoginPermissionFieldName(
                                        'whitelistDelete',
                                    )}
                                    control={form.control}
                                    readonly={readonly}
                                />
                            </>
                        )}
                    {(operatorData.licensePlatePermitSettings.active ||
                        productTypes.length > 0) && (
                        <>
                            <ReactHookFormSwitch
                                label={
                                    <Localized
                                        de="Bewilligungen / Produkte - Anzeigen"
                                        fr="Autorisations / Produits - Afficher"
                                        it="Autorizzazioni / prodotti – Visualizzare"
                                        en="Permits / Products - View"
                                    />
                                }
                                name={operatorLoginPermissionFieldName(
                                    'permitsRead',
                                )}
                                control={form.control}
                                readonly={readonly}
                                onChange={(_e, checked) => {
                                    if (!checked) {
                                        form.setValue(
                                            operatorLoginPermissionFieldName(
                                                'permitsWrite',
                                            ),
                                            false as never,
                                        );
                                        form.setValue(
                                            operatorLoginPermissionFieldName(
                                                'refundPermits',
                                            ),
                                            false as never,
                                        );
                                    }
                                }}
                            />
                            <ReactHookFormSwitch
                                label={
                                    <Localized
                                        de="Bewilligungen / Produkte - Erfassen / Ändern"
                                        fr="Autorisations / Produits - Saisir / modifier"
                                        it="Autorizzazioni / prodotti – Registrare / modificare"
                                        en="Permits / Products - Create / Edit"
                                    />
                                }
                                name={operatorLoginPermissionFieldName(
                                    'permitsWrite',
                                )}
                                control={form.control}
                                readonly={readonly || !permitsRead}
                            />
                            <ReactHookFormSwitch
                                label={
                                    <Localized
                                        de="Bewilligungen / Produkte - Zurückzahlen"
                                        fr="Autorisations / Produits - Rembourser"
                                        it="Autorizzazioni / prodotti – Rimborsare"
                                        en="Permits / Products - Refund"
                                    />
                                }
                                name={operatorLoginPermissionFieldName(
                                    'refundPermits',
                                )}
                                control={form.control}
                                readonly={readonly || !permitsRead}
                            />
                            <ReactHookFormSwitch
                                label={
                                    <Localized
                                        de="Kontingente - Ändern"
                                        fr="Contingents - Modifier"
                                        it="Contingenti - Modificare"
                                        en="Quotas – Edit"
                                    />
                                }
                                name={operatorLoginPermissionFieldName(
                                    'quotasWrite',
                                )}
                                control={form.control}
                                readonly={readonly}
                            />
                            {operatorData.hasParkingaboUserInitialBalanceActive && (
                                <ReactHookFormSwitch
                                    label={
                                        <Localized
                                            de="Saldoguthaben - Erfassen"
                                            fr="Solde - Saisir"
                                            it="Saldo - Registra"
                                            en="Balance – Create"
                                        />
                                    }
                                    name={operatorLoginPermissionFieldName(
                                        'parkingaboInitialBalanceCreate',
                                    )}
                                    control={form.control}
                                    readonly={readonly}
                                />
                            )}
                            {operatorData.hasParkingaboUserInitialBalanceActive && (
                                <ReactHookFormSwitch
                                    label={
                                        <Localized
                                            de="Saldoguthaben - Erfassen"
                                            fr="Solde - Saisir"
                                            it="Saldo - Registrare"
                                            en="Balance – Create"
                                        />
                                    }
                                    name={operatorLoginPermissionFieldName(
                                        'parkingaboInitialBalanceCreate',
                                    )}
                                    control={form.control}
                                    readonly={readonly}
                                />
                            )}
                        </>
                    )}
                    {operatorData.licensePlatePermitSettings.active && (
                        <OperatorLoginPermittypePermissionSelect
                            form={form}
                            readonly={readonly}
                        />
                    )}
                    {productTypes.length > 0 && (
                        <OperatorLoginProductPermissionSelect
                            form={form}
                            readonly={readonly}
                            productTypes={productTypes}
                        />
                    )}
                </>
            )}
        </Stack>
    );
}

function OperatorLoginPermittypePermissionSelect({
    form,
    readonly,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
}) {
    const { storeState } = useStore(store => ({
        permitTypes: PermitTypeState.get(store),
    }));
    const selectablePermitTypes = storeState.permitTypes.data.filter(
        permit =>
            permit.operatorState == 'ACTIVE' || permit.hasNonArchivedContracts,
    );
    const allowedPermitTypes = useWatch({
        control: form.control,
        name: OperatorLoginDetailFormFields.allowedPermitTypes,
    });
    const hasMissingPermitTypes = selectablePermitTypes.some(
        permitType => !allowedPermitTypes.includes(permitType.id),
    );

    return (
        <Box sx={{ paddingBottom: theme => theme.spacing(1) }}>
            <OperatorFormSelectMultiple
                readonly={readonly}
                label={
                    <Localized
                        de="Bewilligungsarten"
                        fr="Types d'autorisations"
                        it="Tipi d'autorizzazione"
                        en="Permit types"
                    />
                }
                name={OperatorLoginDetailFormFields.allowedPermitTypes}
                control={form.control}
                options={selectablePermitTypes.map(permitType => ({
                    type: MultiSelectEntryType.ITEM,
                    display: <PermitSelectionDisplay permitType={permitType} />,
                    value: permitType.id,
                }))}
                infoBox={
                    hasMissingPermitTypes ? (
                        <MultiSelectionInfoBox>
                            <Localized
                                de="Neue Bewilligungsarten werden nicht automatisch zugelassen."
                                fr="Les nouveaux types d'autorisation ne sont pas automatiquement autorisés."
                                it="I nuovi tipi di autorizzazione non vengono autorizzati automaticamente."
                                en="New permit types are not automatically allowed."
                            />
                        </MultiSelectionInfoBox>
                    ) : undefined
                }
            />
        </Box>
    );
}

function PermitSelectionDisplay({ permitType }: { permitType: PermitType }) {
    return (
        <Box>
            {permitType.description}
            {permitType.operatorState === 'INACTIVE' && (
                <Localized
                    de=" — INAKTIV"
                    fr=" — INACTIVE"
                    it=" — INATTIVA"
                    en=" — INACTIVE"
                />
            )}
        </Box>
    );
}

function OperatorLoginProductPermissionSelect({
    form,
    readonly,
    productTypes,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
    productTypes: ProductType[];
}) {
    const { language } = useLanguage();
    const allowedProductTypes = useWatch({
        control: form.control,
        name: OperatorLoginDetailFormFields.allowedProductTypes,
    });
    const hasMissingProductTypes = productTypes.some(
        productType =>
            !allowedProductTypes.includes(productType.contractTemplateId),
    );

    return (
        <Box sx={{ paddingBottom: theme => theme.spacing(1) }}>
            <OperatorFormSelectMultiple
                readonly={readonly}
                label={
                    <Localized
                        de="Produkttypen"
                        fr="Types de produits"
                        it="Tipi di prodotto"
                        en="Product types"
                    />
                }
                name={OperatorLoginDetailFormFields.allowedProductTypes}
                control={form.control}
                options={productTypes
                    .sort((a, b) =>
                        a.productName[language].localeCompare(
                            b.productName[language],
                        ),
                    )
                    .map(productType => ({
                        type: MultiSelectEntryType.ITEM,
                        display: <Localized {...productType.productName} />,
                        value: productType.contractTemplateId,
                    }))}
                infoBox={
                    hasMissingProductTypes ? (
                        <MultiSelectionInfoBox>
                            <Localized
                                de="Neue Produkttypen werden nicht automatisch zugelassen."
                                fr="Les nouveaux types de produits ne sont pas automatiquement autorisés."
                                it="I nuovi tipi di prodotto non vengono autorizzati automaticamente."
                                en="New product types are not automatically allowed."
                            />
                        </MultiSelectionInfoBox>
                    ) : undefined
                }
            />
        </Box>
    );
}

export function OperatorLoginParkingaboModule({
    form,
    readonly,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
}) {
    const parkingaboUserEdit = form.watch(
        operatorLoginPermissionFieldName('parkingaboUserEdit'),
    );
    return (
        <Stack spacing={1}>
            <ParkingaboLoginDivideBar title={<>Parkingabo</>} />
            <ReactHookFormSwitch
                label={
                    <Localized
                        de="Benutzerkonti verwalten"
                        fr="Gérer les comptes utilisateurs"
                        it="Gestire i conti utente"
                        en="Manage user accounts"
                    />
                }
                name={operatorLoginPermissionFieldName('parkingaboUserEdit')}
                control={form.control}
                readonly={readonly}
            />
            {parkingaboUserEdit && (
                <OperatorLoginParkingaboRoleSelect
                    form={form}
                    readonly={readonly}
                />
            )}
        </Stack>
    );
}

function OperatorLoginParkingaboRoleSelect({
    form,
    readonly,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
}) {
    const [rolesState] = useServerFetch<ParkingaboRole[], object>(
        () => ({
            url: `/ui-api/operator-account/parkingabo/roles`,
        }),
        {},
    );

    return (
        <OperatorAsyncLoadedSection
            requestState={rolesState}
            render={roles => (
                <OperatorLoginParkingaboRoleSelectBody
                    form={form}
                    readonly={readonly}
                    roles={roles}
                />
            )}
        />
    );
}

function OperatorLoginParkingaboRoleSelectBody({
    form,
    readonly,
    roles,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
    roles: ParkingaboRole[];
}) {
    const { language } = useLanguage();
    const allowedParkingaboRoles = useWatch({
        control: form.control,
        name: OperatorLoginDetailFormFields.allowedParkingaboRoles,
    });

    if (roles.length <= 1) {
        return null;
    }

    const hasMissingRoles = roles.some(
        role => !allowedParkingaboRoles.includes(role.roleId),
    );

    return (
        <Box sx={{ paddingBottom: theme => theme.spacing(1) }}>
            <OperatorFormSelectMultiple
                readonly={readonly}
                label={
                    <Localized de="Rollen" fr="Rôles" it="Ruoli" en="Roles" />
                }
                name={OperatorLoginDetailFormFields.allowedParkingaboRoles}
                control={form.control}
                options={roles
                    .sort((a, b) => {
                        if (a.deactivated !== b.deactivated) {
                            return a.deactivated ? 1 : -1;
                        }
                        return a.name[language].localeCompare(b.name[language]);
                    })
                    .map(role => ({
                        type: MultiSelectEntryType.ITEM,
                        display: (
                            <Box>
                                <Localized {...role.name} />
                                {role.deactivated && (
                                    <Localized
                                        de=" — INAKTIV"
                                        fr=" — INACTIVE"
                                        it=" — INATTIVA"
                                        en=" — INACTIVE"
                                    />
                                )}
                            </Box>
                        ),
                        value: role.roleId,
                    }))}
                infoBox={
                    hasMissingRoles ? (
                        <MultiSelectionInfoBox>
                            <Localized
                                de="Neue Rollen werden nicht automatisch zugelassen."
                                fr="Les nouveaux rôles ne sont pas automatiquement autorisés."
                                it="I nuovi ruoli non vengono autorizzati automaticamente."
                                en="New roles are not automatically allowed."
                            />
                        </MultiSelectionInfoBox>
                    ) : undefined
                }
            />
        </Box>
    );
}

export function OperatorLoginCloudConnectorModule({
    form,
    readonly,
    recodeConditions,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
    recodeConditions: RecodeCondition[];
}) {
    const { operatorData } = useOperatorContext();
    const cloudConnectorRecodeTicket = useWatch({
        control: form.control,
        name: operatorLoginPermissionFieldName('cloudConnectorRecodeTicket'),
    });
    const eventsRead = useWatch({
        control: form.control,
        name: operatorLoginPermissionFieldName('eventsRead'),
    });
    return (
        <Stack spacing={1}>
            <ParkingaboLoginDivideBar
                title={
                    <Localized
                        de="Cloud Connector"
                        fr="Cloud Connector"
                        it="Cloud Connector"
                        en="Cloud Connector"
                    />
                }
            />
            <ReactHookFormSwitch
                label={
                    <Localized
                        de="Geräte und Zähler"
                        fr="Appareils et compteurs"
                        it="Apparecchi e contatori"
                        en="Devices and counters"
                    />
                }
                name={operatorLoginPermissionFieldName(
                    'cloudConnectorOffstreetDevicesView',
                )}
                control={form.control}
                readonly={readonly}
            />
            <ReactHookFormSwitch
                label={
                    <Localized
                        de="Rabatt - Erfassen"
                        fr="Rabais - Saisir"
                        it="Ribasso - Registrare"
                        en="Discount - Create"
                    />
                }
                name={operatorLoginPermissionFieldName(
                    'cloudConnectorRecodeTicket',
                )}
                control={form.control}
                readonly={readonly}
                onChange={(_e, checked) => {
                    if (!checked) {
                        form.setValue(
                            operatorLoginPermissionFieldName(
                                'cloudConnectorAutoRecode',
                            ),
                            false as never,
                        );
                    }
                }}
            />
            <ReactHookFormSwitch
                label={
                    <Localized
                        de="Autom. Rabatt - Konfigurieren"
                        fr="Rabais autom. - Configurer"
                        it="Ribasso autom. - Configura"
                        en="Auto discount - Configure"
                    />
                }
                name={operatorLoginPermissionFieldName(
                    'cloudConnectorAutoRecode',
                )}
                control={form.control}
                readonly={readonly || !cloudConnectorRecodeTicket}
            />
            {cloudConnectorRecodeTicket && (
                <OperatorLoginRecodeConditionPermissionSelect
                    form={form}
                    readonly={readonly}
                    recodeConditions={recodeConditions}
                />
            )}
            <ReactHookFormSwitch
                label={
                    <Localized
                        de="E-Mail-Benachrichtigungen"
                        fr="Notifications e-mail"
                        it="Notifiche e-mail"
                        en="E-mail alerts"
                    />
                }
                name={operatorLoginPermissionFieldName('cloudConnectorAlerts')}
                control={form.control}
                readonly={readonly}
            />
            {operatorData.hasOffstreetEvents && (
                <>
                    <ReactHookFormSwitch
                        label={
                            <Localized
                                de="Veranstaltungskarten"
                                fr="Cartes manifestation"
                                it="Carte evento"
                                en="Event cards"
                            />
                        }
                        name={operatorLoginPermissionFieldName('eventsRead')}
                        control={form.control}
                        readonly={readonly}
                        onChange={(_e, checked) => {
                            form.setValue(
                                operatorLoginPermissionFieldName('eventsWrite'),
                                checked as never,
                            );
                        }}
                    />
                    {eventsRead && (
                        <Box sx={{ pl: 8 }}>
                            <ReactHookFormSwitch
                                label={
                                    <Localized
                                        de="Erfassen / Ändern"
                                        fr="Saisir / Modifier"
                                        it="Registrare / Modificare"
                                        en="Create / Edit"
                                    />
                                }
                                name={operatorLoginPermissionFieldName(
                                    'eventsWrite',
                                )}
                                control={form.control}
                                readonly={readonly}
                            />
                        </Box>
                    )}
                </>
            )}
            <ReactHookFormSwitch
                label={
                    <Localized
                        de="Reporting Zugriff"
                        fr="Accès au reporting"
                        it="Accesso ai rapporti"
                        en="Reporting access"
                    />
                }
                name={operatorLoginPermissionFieldName(
                    'cloudConnectorReportingAccess',
                )}
                control={form.control}
                readonly={readonly}
            />
        </Stack>
    );
}

function OperatorLoginRecodeConditionPermissionSelect({
    form,
    readonly,
    recodeConditions,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
    recodeConditions: RecodeCondition[];
}) {
    const { language } = useLanguage();
    const allowedRecodeConditions = useWatch({
        control: form.control,
        name: OperatorLoginDetailFormFields.allowedRecodeConditions,
    });
    const hasMissingRecodeConditions = recodeConditions.some(
        recodeCondition =>
            !allowedRecodeConditions.includes(
                recodeCondition.recodeConditionId,
            ),
    );

    return (
        <Box sx={{ paddingBottom: theme => theme.spacing(1) }}>
            <OperatorFormSelectMultiple
                readonly={readonly}
                label={
                    <Localized
                        de="Rabattbedingungen"
                        fr="Conditions de rabais"
                        it="Condizioni ribasso"
                        en="Discount conditions"
                    />
                }
                name={OperatorLoginDetailFormFields.allowedRecodeConditions}
                control={form.control}
                options={recodeConditions
                    .sort((a, b) =>
                        a.description[language].localeCompare(
                            b.description[language],
                        ),
                    )
                    .map(recodeCondition => ({
                        type: MultiSelectEntryType.ITEM,
                        display: (
                            <>
                                <Localized {...recodeCondition.description} />
                                {recodeCondition.deactivated && (
                                    <Localized
                                        de=" — INAKTIV"
                                        fr=" — INACTIVE"
                                        it=" — INATTIVA"
                                        en=" — INACTIVE"
                                    />
                                )}
                            </>
                        ),
                        value: recodeCondition.recodeConditionId,
                    }))}
                infoBox={
                    hasMissingRecodeConditions ? (
                        <MultiSelectionInfoBox>
                            <Localized
                                de="Neue Rabattbedingungen werden nicht automatisch zugelassen."
                                fr="Les nouvelles conditions de rabais ne sont pas automatiquement autorisées."
                                it="Le nuove condizioni di ribasso non sono autorizzate automaticamente."
                                en="New discount conditions are not automatically allowed."
                            />
                        </MultiSelectionInfoBox>
                    ) : undefined
                }
            />
        </Box>
    );
}

export function OperatorLoginTomEcoModule({
    form,
    readonly,
    operatorData,
}: {
    form: UseFormReturn<OperatorLoginDetailFormValues>;
    readonly: boolean;
    operatorData: OperatorDataState.OperatorData;
}) {
    const taxomexModule = useWatch({
        control: form.control,
        name: operatorLoginPermissionFieldName('taxomexModule'),
    });
    return (
        <Stack spacing={1}>
            <ParkingaboLoginDivideBar title={<>TOM eco</>} />
            {operatorData.hasParkingpayModule &&
                operatorData.hasTaxomexModule && (
                    <ReactHookFormSwitch
                        label={
                            <Localized
                                de="TOMeco Modul"
                                fr="Module TOMeco"
                                it="Modulo TOM eco attivo"
                                en="TOMeco module"
                            />
                        }
                        name={operatorLoginPermissionFieldName('taxomexModule')}
                        control={form.control}
                        readonly={readonly}
                        onChange={() => {
                            form.setValue(
                                operatorLoginPermissionFieldName('syncApp'),
                                false as never,
                            );
                            form.setValue(
                                operatorLoginPermissionFieldName(
                                    'usbStickEdit',
                                ),
                                false as never,
                            );
                            form.setValue(
                                operatorLoginPermissionFieldName(
                                    'parkingMeterEdit',
                                ),
                                false as never,
                            );
                            form.setValue(
                                operatorLoginPermissionFieldName(
                                    'parkingMeterAlerts',
                                ),
                                false as never,
                            );
                        }}
                    />
                )}
            {taxomexModule && (
                <>
                    <ReactHookFormSwitch
                        label={
                            <Localized
                                de="Sync Applikation"
                                fr="Synchroniser application"
                                it="Applicazione di sincronizzazione"
                                en="Sync app"
                            />
                        }
                        name={operatorLoginPermissionFieldName('syncApp')}
                        control={form.control}
                        readonly={readonly}
                    />
                    <ReactHookFormSwitch
                        label={
                            <Localized
                                de="USB Sticks - Ändern"
                                fr="Sticks USB - Modifier"
                                it="Modificare stick USB"
                                en="USB stick - Modify"
                            />
                        }
                        name={operatorLoginPermissionFieldName('usbStickEdit')}
                        control={form.control}
                        readonly={readonly}
                    />
                    <ReactHookFormSwitch
                        label={
                            <Localized
                                de="Parkuhren - Ändern"
                                fr="Parcmètres - Modifier"
                                it="Modificare parchimetri"
                                en="Parking meters- Edit"
                            />
                        }
                        name={operatorLoginPermissionFieldName(
                            'parkingMeterEdit',
                        )}
                        control={form.control}
                        readonly={readonly}
                    />
                    <ReactHookFormSwitch
                        label={
                            <Localized
                                de="E-Mail-Benachrichtigungen"
                                fr="Notifications e-mail"
                                it="Notifiche e-mail"
                                en="E-mail alerts"
                            />
                        }
                        name={operatorLoginPermissionFieldName(
                            'parkingMeterAlerts',
                        )}
                        control={form.control}
                        readonly={readonly}
                    />
                </>
            )}
        </Stack>
    );
}

export function ParkingaboLoginDivideBar({ title }: { title: JSX.Element }) {
    return (
        <Box
            sx={{
                ...OperatorTypo.headingOne,
                textTransform: 'uppercase',
                fontWeight: '600',
                color: theme => theme.palette.blue.main,
                backgroundColor: theme => theme.palette.grey.A200,
            }}
        >
            <Box sx={{ textAlign: 'center', margin: '2px 0' }}>{title}</Box>
        </Box>
    );
}
